import { defineStore } from "pinia";
import type {
  Patient,
  PatientGroup,
  PatientCategory,
  SortColumn,
  SortDirection,
  UnitOption,
} from "@virtual-care/shared";
import { patientService } from "../services/patientService";
import { patientApiService } from "../services/patientApiService";
import { callLogService } from "../services/callLogService";

const OVERDUE_THRESHOLD_HOURS = 6;

function isPatientOverdue(patient: Patient): boolean {
  if (!patient.dischargeOrderTime) return false;
  const now = Date.now();
  const orderTime = new Date(patient.dischargeOrderTime).getTime();
  const hoursSinceOrder = (now - orderTime) / (1000 * 60 * 60);
  return hoursSinceOrder > OVERDUE_THRESHOLD_HOURS;
}

interface PatientStoreState {
  patients: Patient[];
  facilityId: string;
  units: UnitOption[];
  selectedUnit: string | null;
  selectedFilters: Set<PatientCategory>;
  searchQuery: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  expandedGroups: Set<string>;
  loading: boolean;
  error: Error | null;
}

export const usePatientStore = defineStore("patient", {
  state: (): PatientStoreState => ({
    patients: [],
    facilityId: "fac-001",
    units: [],
    selectedUnit: null,
    selectedFilters: new Set<PatientCategory>(),
    searchQuery: "",
    sortColumn: null,
    sortDirection: null,
    expandedGroups: new Set<string>(["discharge-ready"]),
    loading: false,
    error: null,
  }),

  getters: {
    filteredPatients(state): Patient[] {
      let filtered = [...state.patients];

      if (state.selectedFilters.size > 0) {
        filtered = filtered.filter((patient) => state.selectedFilters.has(patient.category));
      }

      if (state.searchQuery.trim()) {
        filtered = patientService.searchPatients(state.searchQuery, filtered);
      }

      filtered = patientService.sortPatients(filtered, state.sortColumn, state.sortDirection);

      return filtered;
    },

    patientGroups(state): PatientGroup[] {
      const filtered = this.filteredPatients;

      const PRIMARY_CATEGORIES = ["discharge-ready", "condition-discharge", "new-admit"];

      const groupMap = new Map<string, Patient[]>();
      groupMap.set("discharge-ready", []);
      groupMap.set("condition-discharge", []);
      groupMap.set("new-admit", []);
      groupMap.set("other", []);

      filtered.forEach((patient: Patient) => {
        const category = patient.category;
        if (PRIMARY_CATEGORIES.includes(category)) {
          groupMap.get(category)!.push(patient);
        } else {
          groupMap.get("other")!.push(patient);
        }
      });

      const groups: PatientGroup[] = [];

      const groupConfigs = [
        {
          id: "discharge-ready",
          title: "Discharge Ready Patients",
          targetResponse: "Target response: within 360 minutes of discharge order",
        },
        {
          id: "condition-discharge",
          title: "Conditional Discharge Patients",
          targetResponse: "Target response: Monitor conditions for discharge readiness",
        },
        {
          id: "new-admit",
          title: "New Admit Patients",
          targetResponse: "Target response: Admission intake completed within 6 hours of admission",
        },
        {
          id: "other",
          title: "Other Patients",
          targetResponse: "Target response: As per protocol",
        },
      ];

      for (const config of groupConfigs) {
        let patients = groupMap.get(config.id)!;

        // Only apply default overdue-first sorting when user hasn't selected a sort
        if (config.id === "discharge-ready" && state.sortColumn === null) {
          patients = [...patients].sort((a, b) => {
            const aOverdue = isPatientOverdue(a);
            const bOverdue = isPatientOverdue(b);
            if (aOverdue && !bOverdue) return -1;
            if (!aOverdue && bOverdue) return 1;
            return 0;
          });
        }

        if (patients.length > 0) {
          groups.push({
            id: config.id,
            title: config.title,
            count: patients.length,
            targetResponse: config.targetResponse,
            patients,
            category: config.id as PatientCategory,
            isExpanded: state.expandedGroups.has(config.id),
          });
        }
      }

      return groups;
    },

    totalPatientCount(state): number {
      return state.patients.length;
    },

    filterCounts(state): {
      overdue: number;
      "discharge-ready": number;
      "condition-discharge": number;
      "new-admit": number;
      other: number;
    } {
      const PRIMARY_CATEGORIES = ["discharge-ready", "condition-discharge", "new-admit"];

      const counts = {
        overdue: 0,
        "discharge-ready": 0,
        "condition-discharge": 0,
        "new-admit": 0,
        other: 0,
      };

      state.patients.forEach((patient) => {
        const overdue = isPatientOverdue(patient);
        if (overdue) {
          counts.overdue++;
        }
        if (patient.category === "discharge-ready") {
          counts["discharge-ready"]++;
        }
        if (patient.category === "condition-discharge") {
          counts["condition-discharge"]++;
        }
        if (patient.category === "new-admit") {
          counts["new-admit"]++;
        }
        if (!PRIMARY_CATEGORIES.includes(patient.category)) {
          counts.other++;
        }
      });

      return counts;
    },
  },

  actions: {
    async loadUnits() {
      try {
        const units = await patientApiService.getUnitsByFacility(this.facilityId);
        this.units = units.map((u) => ({ id: u.unitId, name: u.unitName }));

        if (units.length > 0 && !this.selectedUnit) {
          this.selectedUnit = units[0].unitId;
        }
      } catch (error) {
        console.error("Failed to load units:", error);
        this.error = error as Error;
      }
    },

    setSelectedUnit(unitId: string) {
      this.selectedUnit = unitId;
      this.loadPatients(unitId);
    },

    async loadPatients(unitId?: string) {
      this.loading = true;
      this.error = null;

      try {
        const patients = await patientService.getPatients(unitId);
        this.patients = patients;
        this.loading = false;

        // Fetch call logs in parallel batches (background)
        const patientIds = patients.map((p) => p.patientId);
        const batchSize = 10;

        for (let i = 0; i < patientIds.length; i += batchSize) {
          const batch = patientIds.slice(i, i + batchSize);

          await Promise.all(
            batch.map(async (patientId) => {
              try {
                const callLogData = await callLogService.getMostRecentWithCount(patientId);
                const patient = this.patients.find((p) => p.patientId === patientId);
                if (patient) {
                  patient.recentCallLog = callLogData.callLog;
                  patient.totalCallCount = callLogData.totalCount;
                }
              } catch (error) {
                console.warn(`Failed to fetch call logs for ${patientId}`, error);
              }
            })
          );
        }
      } catch (error) {
        this.error = error as Error;
        console.error("Failed to load patients:", error);
        this.loading = false;
      }
    },

    async refreshPatientCallLog(patientId: string) {
      try {
        const callLogData = await callLogService.getMostRecentWithCount(patientId);
        const patient = this.patients.find((p) => p.patientId === patientId);
        if (patient) {
          patient.recentCallLog = callLogData.callLog;
          patient.totalCallCount = callLogData.totalCount;
        }
      } catch (error) {
        console.warn(`[patientStore] Failed to refresh call log`, error);
      }
    },

    setPatientAvailability(patientId: string, response: 'available' | 'not_now') {
      const patient = this.patients.find((p) => p.patientId === patientId);
      if (patient) {
        // Store availability response with timestamp
        patient.availabilityResponse = {
          response,
          timestamp: Date.now(),
        };
        console.log('[patientStore] Updated patient availability:', patientId, response);
      }
    },

    toggleFilter(category: PatientCategory) {
      if (this.selectedFilters.has(category)) {
        this.selectedFilters.delete(category);
      } else {
        this.selectedFilters.add(category);
      }
      this.selectedFilters = new Set(this.selectedFilters);
    },

    clearFilters() {
      this.selectedFilters.clear();
      this.selectedFilters = new Set();
    },

    setSearchQuery(query: string) {
      this.searchQuery = query;
    },

    setSortColumn(column: SortColumn) {
      if (this.sortColumn === column) {
        if (this.sortDirection === "ASC") {
          this.sortDirection = "DESC";
        } else if (this.sortDirection === "DESC") {
          this.sortColumn = null;
          this.sortDirection = null;
        } else {
          this.sortDirection = "ASC";
        }
      } else {
        this.sortColumn = column;
        this.sortDirection = "ASC";
      }
    },

    toggleGroupExpanded(groupId: string) {
      if (this.expandedGroups.has(groupId)) {
        this.expandedGroups.delete(groupId);
      } else {
        this.expandedGroups.add(groupId);
      }
      this.expandedGroups = new Set(this.expandedGroups);
    },
  },
});
